/**
 *  @namespace DeliveryTime
 *  Splits cart quantities between the local stock in Viborg and the remote warehouse,
 *  and stores the delivery time as visible line item properties.
 *  Stock data comes from snippets/delivery-time-data.liquid.
 */
window.DeliveryTime = (() => {
  // Flow caps the stock metafields at 40, so 40 means "at least 40" and anything above is counted as remote.
  const LOCAL_STOCK_CAP = 40;
  const CART_DATA_SECTION = 'cart-delivery-time-data';
  const KNOWN_LABELS_STORAGE_KEY = 'deliveryTime:knownLabels';
  const LAST_LOCALE_STORAGE_KEY = 'deliveryTime:lastLocale';
  const MAX_KNOWN_LABELS = 10;

  const strings = () => window.theme.deliveryTime;

  function readStorage(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key));
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      // Storage can be blocked; recognition then falls back to the current language only.
    }
  }

  /**
   *  Lines keep the language they were added in, so the labels of every language seen in this
   *  browser are remembered. That lets a language switch replace old lines instead of duplicating them.
   *  @returns {Array} [{ propertyName, localLabel }], current language first
   */
  function rememberCurrentLabels() {
    const { propertyName, localLabel } = strings();
    const storedLabels = readStorage(KNOWN_LABELS_STORAGE_KEY);
    const otherLabels = (Array.isArray(storedLabels) ? storedLabels : []).filter((labels) => {
      return labels && labels.propertyName && !(labels.propertyName === propertyName && labels.localLabel === localLabel);
    });
    const knownLabels = [{ propertyName, localLabel }, ...otherLabels].slice(0, MAX_KNOWN_LABELS);
    writeStorage(KNOWN_LABELS_STORAGE_KEY, knownLabels);
    return knownLabels;
  }

  // Returns true when the storefront language differs from the previous page view.
  function rememberCurrentLocale() {
    const lastLocale = readStorage(LAST_LOCALE_STORAGE_KEY);
    writeStorage(LAST_LOCALE_STORAGE_KEY, strings().locale);
    return Boolean(lastLocale) && lastLocale !== strings().locale;
  }

  const knownLabels = rememberCurrentLabels();
  const deliveryPropertyNames = new Set(knownLabels.map((labels) => labels.propertyName));

  function toStockNumber(value) {
    const number = Math.floor(Number(value));
    return Number.isFinite(number) && number > 0 ? number : 0;
  }

  function normalizeVariant(raw) {
    return {
      localStock: toStockNumber(raw.localStock),
      remoteStock: toStockNumber(raw.remoteStock),
      inventoryQuantity: Math.floor(Number(raw.inventoryQuantity)) || 0,
      hasStockMetafields: raw.hasStockMetafields === true,
      canPreorder: raw.canPreorder === true,
      requiresShipping: raw.requiresShipping !== false,
      remoteDeliveryTime: String(raw.remoteDeliveryTime || '')
    };
  }

  function readVariantData(root = document) {
    const variantData = {};
    root.querySelectorAll('[data-delivery-time-data]').forEach((script) => {
      try {
        const parsed = JSON.parse(script.textContent);
        Object.keys(parsed).forEach((variantId) => {
          variantData[variantId] = normalizeVariant(parsed[variantId]);
        });
      } catch (e) {
        console.error(e);
      }
    });
    return variantData;
  }

  /**
   *  Same rules as the quick_info block:
   *  local stock > remote stock > pre-order, and Shopify's total stock when the metafields are missing.
   */
  function getStockStatus(variant) {
    if (!variant.hasStockMetafields) {
      if (variant.inventoryQuantity > 0) return 'shopify_stock';
      return variant.canPreorder ? 'preorder' : 'unavailable';
    }
    if (variant.localStock > 0) return 'local';
    if (variant.remoteStock > 0) return 'remote';
    return variant.canPreorder ? 'preorder' : 'unavailable';
  }

  function getLocalCapacity(variant) {
    return variant.hasStockMetafields ? Math.min(variant.localStock, LOCAL_STOCK_CAP) : 0;
  }

  function isBlank(value) {
    return value === null || value === undefined || String(value).trim() === '';
  }

  // Blank values are dropped so lines compare equal no matter how Shopify stores empty properties.
  function getBaseProperties(properties) {
    const baseProperties = {};
    Object.keys(properties || {}).forEach((name) => {
      if (deliveryPropertyNames.has(name) || isBlank(properties[name])) return;
      baseProperties[name] = properties[name];
    });
    return baseProperties;
  }

  function getPropertiesSignature(properties) {
    const cleanProperties = {};
    Object.keys(properties || {}).sort().forEach((name) => {
      if (!isBlank(properties[name])) cleanProperties[name] = properties[name];
    });
    return JSON.stringify(cleanProperties);
  }

  function getGroupSignature(variantId, baseProperties, sellingPlanId) {
    return `${variantId}|${sellingPlanId || ''}|${getPropertiesSignature(baseProperties)}`;
  }

  function getLineSellingPlanId(line) {
    return line.selling_plan_allocation ? line.selling_plan_allocation.selling_plan.id : null;
  }

  function isLocalLine(line) {
    if (!line.properties) return false;
    return knownLabels.some(({ propertyName, localLabel }) => line.properties[propertyName] === localLabel);
  }

  // @returns {Array} [{ quantity, properties }] in the current storefront language.
  function buildLines(variant, { localQuantity, remoteQuantity }, baseProperties) {
    const { propertyName, localLabel } = strings();
    const lines = [];

    if (localQuantity > 0) {
      lines.push({
        quantity: localQuantity,
        properties: { ...baseProperties, [propertyName]: localLabel }
      });
    }
    if (remoteQuantity > 0) {
      lines.push({
        quantity: remoteQuantity,
        properties: { ...baseProperties, [propertyName]: variant.remoteDeliveryTime }
      });
    }
    return lines;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });
  }

  function fetchCart() {
    return fetch(`${theme.routes.cart_url}.js`, {
      headers: { 'Accept': 'application/json' }
    }).then((response) => response.json());
  }

  function fetchCartVariantData() {
    const rootUrl = theme.routes.root_url.endsWith('/') ? theme.routes.root_url : `${theme.routes.root_url}/`;
    return fetch(`${rootUrl}?sections=${CART_DATA_SECTION}`)
      .then((response) => response.json())
      .then((sections) => {
        const html = new DOMParser().parseFromString(sections[CART_DATA_SECTION], 'text/html');
        return readVariantData(html);
      });
  }

  /**
   *  Reads id, quantity, selling plan and properties (siblings, custom options, apps) from a product form.
   *  Returns null when the form holds files, which can't be sent as JSON.
   */
  function getCartRequestFromFormData(formData) {
    const request = {
      variantId: formData.get('id'),
      quantity: parseInt(formData.get('quantity'), 10) || 1,
      sellingPlanId: formData.get('selling_plan') || null,
      properties: {}
    };
    let hasFile = false;

    formData.forEach((value, name) => {
      const match = name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      if (value instanceof File) {
        hasFile = hasFile || value.size > 0;
        return;
      }
      request.properties[match[1]] = value;
    });

    return hasFile ? null : request;
  }

  /**
   *  Builds the items array for /cart/add.js. Local units already in the cart for this variant
   *  are subtracted from the Viborg stock first.
   */
  async function buildCartItems({ variantId, quantity, sellingPlanId = null, properties = {} }, variantData) {
    const variant = variantData[variantId];
    const baseProperties = getBaseProperties(properties);
    const sellingPlan = sellingPlanId ? { selling_plan: sellingPlanId } : {};

    if (!variant || !variant.requiresShipping) {
      return [{ id: variantId, quantity, properties: baseProperties, ...sellingPlan }];
    }

    const cart = await fetchCart();
    const localLinesInCart = cart.items.filter((line) => String(line.variant_id) === String(variantId) && isLocalLine(line));
    const reservedLocalQuantity = localLinesInCart.reduce((sum, line) => sum + line.quantity, 0);

    const localQuantity = Math.min(quantity, Math.max(getLocalCapacity(variant) - reservedLocalQuantity, 0));
    const lines = buildLines(variant, {
      localQuantity,
      remoteQuantity: quantity - localQuantity
    }, baseProperties);

    return lines.map((line) => ({ id: variantId, ...line, ...sellingPlan }));
  }

  function groupCartLines(cartItems, variantData) {
    const groups = new Map();

    cartItems.forEach((line) => {
      const variant = variantData[line.variant_id];
      if (!variant || !variant.requiresShipping) return;

      const baseProperties = getBaseProperties(line.properties);
      const sellingPlanId = getLineSellingPlanId(line);
      const signature = getGroupSignature(line.variant_id, baseProperties, sellingPlanId);

      if (!groups.has(signature)) {
        groups.set(signature, {
          variantId: line.variant_id,
          variant,
          baseProperties,
          sellingPlanId,
          lines: [],
          quantity: 0,
          currentLocalQuantity: 0
        });
      }
      const group = groups.get(signature);
      group.lines.push(line);
      group.quantity += line.quantity;
      if (isLocalLine(line)) group.currentLocalQuantity += line.quantity;
    });

    return Array.from(groups.values());
  }

  function distributeLocalCapacity(groups) {
    const remainingCapacity = new Map();
    groups.forEach((group) => {
      if (!remainingCapacity.has(group.variantId)) {
        remainingCapacity.set(group.variantId, getLocalCapacity(group.variant));
      }
    });

    // Existing local lines keep their units first, so changing one line never moves units on another.
    groups.forEach((group) => {
      const capacity = remainingCapacity.get(group.variantId);
      group.localQuantity = Math.min(group.currentLocalQuantity, group.quantity, capacity);
      remainingCapacity.set(group.variantId, capacity - group.localQuantity);
    });

    groups.forEach((group) => {
      const capacity = remainingCapacity.get(group.variantId);
      const extraLocalQuantity = Math.min(group.quantity - group.localQuantity, capacity);
      group.localQuantity += extraLocalQuantity;
      remainingCapacity.set(group.variantId, capacity - extraLocalQuantity);
    });
  }

  /**
   *  Compares every cart line with the wanted split and returns the smallest set of changes.
   *  @returns {Object} { additions: [] for /cart/add.js, updates: {} for /cart/update.js }
   */
  function planCartChanges(cartItems, variantData) {
    const groups = groupCartLines(cartItems, variantData);
    const additions = [];
    const updates = {};

    distributeLocalCapacity(groups);

    groups.forEach((group) => {
      const wantedLines = buildLines(group.variant, {
        localQuantity: group.localQuantity,
        remoteQuantity: group.quantity - group.localQuantity
      }, group.baseProperties);
      const linesBySignature = new Map(group.lines.map((line) => [getPropertiesSignature(line.properties), line]));
      const keptLineKeys = new Set();

      wantedLines.forEach((wantedLine) => {
        const existingLine = linesBySignature.get(getPropertiesSignature(wantedLine.properties));
        if (existingLine) {
          keptLineKeys.add(existingLine.key);
          if (existingLine.quantity !== wantedLine.quantity) updates[existingLine.key] = wantedLine.quantity;
          return;
        }
        additions.push({
          id: group.variantId,
          quantity: wantedLine.quantity,
          properties: wantedLine.properties,
          ...(group.sellingPlanId ? { selling_plan: group.sellingPlanId } : {})
        });
      });

      group.lines.forEach((line) => {
        if (!keptLineKeys.has(line.key)) updates[line.key] = 0;
      });
    });

    return { additions, updates };
  }

  function setCartBusy(isBusy) {
    const cartForm = document.getElementById('Cart');
    if (cartForm) cartForm.classList.toggle('cart-disabled', isBusy);
    document.querySelectorAll('#Cart-Drawer .product-cart-item').forEach((item) => {
      item.classList.toggle('thb-loading', isBusy);
    });
  }

  // New lines are added before old ones are removed, so a failed add never loses items from the cart.
  async function applyCartChanges({ additions, updates }) {
    if (additions.length) {
      const response = await postJSON(`${theme.routes.cart_add_url}.js`, { items: additions });
      if (!response.ok) throw new Error(`Delivery time: could not add split lines (${response.status})`);
    }
    if (Object.keys(updates).length) {
      const response = await postJSON(`${theme.routes.cart_update_url}.js`, { updates });
      if (!response.ok) throw new Error(`Delivery time: could not update cart lines (${response.status})`);
    }
  }

  async function runCartReconciliation() {
    const [cart, variantData] = await Promise.all([fetchCart(), fetchCartVariantData()]);
    const changes = planCartChanges(cart.items, variantData);

    if (!changes.additions.length && !Object.keys(changes.updates).length) return;

    setCartBusy(true);
    try {
      await applyCartChanges(changes);
    } finally {
      setCartBusy(false);
      dispatchCustomEvent('cart:refresh');
    }
  }

  // Queued so overlapping triggers always work on the latest cart instead of racing each other.
  let reconciliationQueue = Promise.resolve();

  function reconcileCart() {
    reconciliationQueue = reconciliationQueue
      .then(runCartReconciliation)
      .catch((e) => console.error(e));
    return reconciliationQueue;
  }

  // Covers quantity changes in the cart page and drawer, and lines added by apps outside the theme.
  document.addEventListener('line-item:change:end', reconcileCart);
  document.addEventListener('cart-drawer:open', reconcileCart);
  document.addEventListener('DOMContentLoaded', () => {
    const localeChanged = rememberCurrentLocale();
    // After a language switch the lines are rewritten right away, so header count and drawer never show both languages.
    if (document.getElementById('Cart') || localeChanged) reconcileCart();
  });

  return {
    readVariantData,
    getStockStatus,
    getLocalCapacity,
    getCartRequestFromFormData,
    buildCartItems,
    planCartChanges,
    reconcileCart,
    strings
  };
})();
